import { DateTime } from "luxon";
import type { InvoiceEntity } from "../../../api/types";
import { matchesAssignedState } from "../../../components/AssignedStateToggle";
import { invoiceSenderKey } from "../../../logic/invoice-sender";
import type { Filters, InvoiceSort } from "./types";

const compareDescIso = (a: string, b: string) =>
  DateTime.fromISO(b).toMillis() - DateTime.fromISO(a).toMillis();

export const sortInvoices = (
  invoices: InvoiceEntity[],
  sortBy: InvoiceSort,
): InvoiceEntity[] => {
  const copy = [...invoices];
  switch (sortBy) {
    case "dueDateDesc":
      copy.sort((a, b) => compareDescIso(a.dueDate, b.dueDate));
      break;
    case "receivedDateDesc":
      copy.sort((a, b) => compareDescIso(a.dateReceived, b.dateReceived));
      break;
    case "nameAsc":
      copy.sort((a, b) => a.name.localeCompare(b.name));
      break;
  }
  return copy;
};

export const filterInvoices = (
  invoices: InvoiceEntity[],
  filters: Filters,
): InvoiceEntity[] => {
  const pattern = filters.namePattern.trim().toLowerCase();
  const from = filters.from
    ? DateTime.fromISO(filters.from).startOf("day")
    : null;
  const to = filters.to ? DateTime.fromISO(filters.to).endOf("day") : null;

  return invoices.filter((item) => {
    if (filters.senderKeys.length > 0) {
      const key = invoiceSenderKey(item);
      if (!key || !filters.senderKeys.includes(key)) return false;
    }
    if (pattern) {
      const haystack = `${item.name} ${item.id}`.toLowerCase();
      if (!haystack.includes(pattern)) return false;
    }
    const hasAttachments = (item.attachments?.length ?? 0) > 0;
    if (!matchesAssignedState(hasAttachments, filters.attachments))
      return false;
    if (from || to) {
      const due = DateTime.fromISO(item.dueDate);
      if (!due.isValid) return false;
      if (from && due < from) return false;
      if (to && due > to) return false;
    }
    return true;
  });
};
